import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Brain, TrendingUp, Users, AlertTriangle, DollarSign, Scale, 
  RefreshCw, CheckCircle, XCircle, Eye, Filter, LayoutGrid, List,
  MapPin, Clock, Package, ChevronRight, Sparkles, Award, Target
} from 'lucide-react';
import { 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, 
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import { toast } from 'sonner';

const SCORE_COLORS = {
  high: '#EF4444',    // >80 vermelho
  medium: '#F97316',  // 60-80 laranja
  low: '#22C55E'      // <60 verde
};

const getScoreColor = (score) => {
  if (score >= 80) return SCORE_COLORS.high;
  if (score >= 60) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
};

const getScoreBgClass = (score) => {
  if (score >= 80) return 'bg-red-500';
  if (score >= 60) return 'bg-orange-500';
  return 'bg-green-500';
};

export default function PriorizacaoPage() {
  const [necessidades, setNecessidades] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [tiposRecursos, setTiposRecursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculando, setRecalculando] = useState(false);
  
  // Filters
  const [filtroDistrito, setFiltroDistrito] = useState('');
  const [filtroTipoRecurso, setFiltroTipoRecurso] = useState('');
  const [filtroScoreMin, setFiltroScoreMin] = useState('');
  const [filtroScoreMax, setFiltroScoreMax] = useState('');
  
  // View mode
  const [viewMode, setViewMode] = useState('list'); // list, grid
  
  // Selection for batch
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Modal
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [selectedNecessidade, setSelectedNecessidade] = useState(null);
  const [detalhesData, setDetalhesData] = useState(null);
  const [detalhesLoading, setDetalhesLoading] = useState(false);

  // Rejection modal
  const [rejeitarModalOpen, setRejeitarModalOpen] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [necData, distData, tiposData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/necessidades/priorizadas?limit=100`).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/tipos`).then(r => r.json())
      ]);
      setNecessidades(necData);
      setDistritos(distData);
      setTiposRecursos(tiposData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const recalcularTodas = async () => {
    setRecalculando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/calcular-todas-priorizacoes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao recalcular priorizações');
    } finally {
      setRecalculando(false);
    }
  };

  const openDetalhes = async (nec) => {
    setSelectedNecessidade(nec);
    setDetalhesModalOpen(true);
    setDetalhesLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/necessidades/${nec.id}/detalhes`);
      const data = await response.json();
      setDetalhesData(data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setDetalhesLoading(false);
    }
  };

  const aprovarNecessidade = async (necId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/necessidades/${necId}/aprovar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Necessidade aprovada!');
      loadData();
      setDetalhesModalOpen(false);
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const rejeitarNecessidade = async () => {
    if (!selectedNecessidade) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/necessidades/${selectedNecessidade.id}/rejeitar?motivo=${encodeURIComponent(motivoRejeicao)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Necessidade rejeitada');
      setRejeitarModalOpen(false);
      setMotivoRejeicao('');
      loadData();
      setDetalhesModalOpen(false);
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const aprovarLote = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione necessidades para aprovar');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recursos/necessidades/aprovar-lote`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedIds)
      });
      const data = await response.json();
      toast.success(data.message);
      setSelectedIds([]);
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar em lote');
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectTop10 = () => {
    setSelectedIds(necessidades.slice(0, 10).map(n => n.id));
  };

  const selectHighScore = () => {
    setSelectedIds(necessidades.filter(n => (n.score_priorizacao || 0) >= 80).map(n => n.id));
  };

  // Filter data
  const filteredNecessidades = necessidades.filter(nec => {
    if (filtroDistrito && nec.distrito_nome !== distritos.find(d => d.id === filtroDistrito)?.nome) return false;
    if (filtroTipoRecurso && nec.tipo_recurso_id !== filtroTipoRecurso) return false;
    if (filtroScoreMin && (nec.score_priorizacao || 0) < parseFloat(filtroScoreMin)) return false;
    if (filtroScoreMax && (nec.score_priorizacao || 0) > parseFloat(filtroScoreMax)) return false;
    return true;
  });

  // Radar chart data for details modal
  const getRadarData = () => {
    if (!selectedNecessidade?.scores_componentes) return [];
    const scores = selectedNecessidade.scores_componentes;
    return [
      { subject: 'Urgência', A: scores.urgencia || 0, fullMark: 100 },
      { subject: 'Impacto', A: scores.impacto || 0, fullMark: 100 },
      { subject: 'Vulnerabilidade', A: scores.vulnerabilidade || 0, fullMark: 100 },
      { subject: 'Capacidade', A: scores.capacidade || 0, fullMark: 100 },
      { subject: 'Equidade', A: scores.equidade || 0, fullMark: 100 },
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="priorizacao-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="priorizacao-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            Priorização Inteligente
          </h1>
          <p className="text-slate-500 mt-1">Algoritmo multi-critério com análise IA para priorização de pedidos</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={recalcularTodas}
            variant="outline"
            disabled={recalculando}
            data-testid="recalcular-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${recalculando ? 'animate-spin' : ''}`} />
            {recalculando ? 'A calcular...' : 'Recalcular Todas'}
          </Button>
          {selectedIds.length > 0 && (
            <Button 
              onClick={aprovarLote}
              className="bg-green-600 hover:bg-green-700"
              data-testid="aprovar-lote-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprovar {selectedIds.length} Selecionadas
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {necessidades.filter(n => (n.score_priorizacao || 0) >= 80).length}
                </p>
                <p className="text-xs text-slate-500">Críticas (80+)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">
                  {necessidades.filter(n => (n.score_priorizacao || 0) >= 60 && (n.score_priorizacao || 0) < 80).length}
                </p>
                <p className="text-xs text-slate-500">Urgentes (60-80)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {necessidades.filter(n => (n.score_priorizacao || 0) < 60).length}
                </p>
                <p className="text-xs text-slate-500">Normais (&lt;60)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">
                  {necessidades.filter(n => n.analise_ia?.recomendacao === 'Prioritário').length}
                </p>
                <p className="text-xs text-slate-500">IA Prioritário</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{necessidades.length}</p>
                <p className="text-xs text-slate-500">Total Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs">Distrito</Label>
              <Select value={filtroDistrito || "all"} onValueChange={(v) => setFiltroDistrito(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filtro-distrito">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {distritos.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs">Tipo Recurso</Label>
              <Select value={filtroTipoRecurso || "all"} onValueChange={(v) => setFiltroTipoRecurso(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filtro-tipo">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {tiposRecursos.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-24">
              <Label className="text-xs">Score Min</Label>
              <Input 
                type="number" 
                placeholder="0" 
                value={filtroScoreMin}
                onChange={(e) => setFiltroScoreMin(e.target.value)}
                data-testid="filtro-score-min"
              />
            </div>
            
            <div className="w-24">
              <Label className="text-xs">Score Max</Label>
              <Input 
                type="number" 
                placeholder="100" 
                value={filtroScoreMax}
                onChange={(e) => setFiltroScoreMax(e.target.value)}
                data-testid="filtro-score-max"
              />
            </div>
            
            <div className="flex gap-1">
              <Button 
                variant={viewMode === 'list' ? 'default' : 'outline'} 
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'grid' ? 'default' : 'outline'} 
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectTop10}>
                Top 10
              </Button>
              <Button variant="outline" size="sm" onClick={selectHighScore}>
                Score 80+
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      {viewMode === 'list' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Ranking de Necessidades ({filteredNecessidades.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-16 text-center">#</TableHead>
                  <TableHead className="w-20 text-center">Score</TableHead>
                  <TableHead>Instituição</TableHead>
                  <TableHead>Recurso</TableHead>
                  <TableHead className="text-center">Qtd</TableHead>
                  <TableHead>Análise</TableHead>
                  <TableHead>IA</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNecessidades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                      Nenhuma necessidade encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNecessidades.map((nec) => (
                    <TableRow 
                      key={nec.id} 
                      className={`hover:bg-slate-50 ${selectedIds.includes(nec.id) ? 'bg-blue-50' : ''}`}
                      data-testid={`necessidade-row-${nec.id}`}
                    >
                      <TableCell>
                        <Checkbox 
                          checked={selectedIds.includes(nec.id)}
                          onCheckedChange={() => toggleSelection(nec.id)}
                          data-testid={`checkbox-${nec.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold ${getScoreBgClass(nec.score_priorizacao || 0)}`}>
                          {nec.ranking || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div 
                          className="text-2xl font-bold"
                          style={{ color: getScoreColor(nec.score_priorizacao || 0) }}
                        >
                          {(nec.score_priorizacao || 0).toFixed(0)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{nec.instituicao_nome}</p>
                        <p className="text-xs text-slate-500">{nec.distrito_nome} • {nec.instituicao_codigo}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{nec.tipo_recurso_nome}</p>
                        <Badge variant="outline" className="text-xs">{nec.tipo_recurso_categoria}</Badge>
                      </TableCell>
                      <TableCell className="text-center font-bold">{nec.quantidade_necessaria}</TableCell>
                      <TableCell>
                        <p className="text-xs max-w-[200px]">{nec.explicacao_priorizacao || 'Não calculado'}</p>
                      </TableCell>
                      <TableCell>
                        {nec.analise_ia?.recomendacao && (
                          <Badge className={
                            nec.analise_ia.recomendacao === 'Prioritário' ? 'bg-red-500' :
                            nec.analise_ia.recomendacao === 'Normal' ? 'bg-blue-500' :
                            'bg-green-500'
                          }>
                            {nec.analise_ia.recomendacao}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openDetalhes(nec)}
                            data-testid={`ver-detalhes-${nec.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => aprovarNecessidade(nec.id)}
                            className="text-green-600"
                            data-testid={`aprovar-${nec.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredNecessidades.map((nec) => (
            <Card 
              key={nec.id} 
              className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${selectedIds.includes(nec.id) ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => openDetalhes(nec)}
              data-testid={`card-${nec.id}`}
            >
              {/* Ranking Badge */}
              <div className={`absolute top-3 left-3 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg ${getScoreBgClass(nec.score_priorizacao || 0)}`}>
                #{nec.ranking || '-'}
              </div>
              
              {/* Score */}
              <div className="absolute top-3 right-3 text-right">
                <div 
                  className="text-3xl font-bold"
                  style={{ color: getScoreColor(nec.score_priorizacao || 0) }}
                >
                  {(nec.score_priorizacao || 0).toFixed(0)}
                </div>
                <p className="text-xs text-slate-400">pontos</p>
              </div>
              
              <CardContent className="pt-20 pb-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-lg truncate">{nec.instituicao_nome}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {nec.distrito_nome}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{nec.tipo_recurso_nome}</p>
                      <p className="text-xs text-slate-500">{nec.quantidade_necessaria} unidades</p>
                    </div>
                    <Badge variant="outline">{nec.tipo_recurso_categoria}</Badge>
                  </div>
                  
                  {/* Mini Radar */}
                  {nec.scores_componentes && (
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={[
                          { subject: 'U', A: nec.scores_componentes.urgencia || 0 },
                          { subject: 'I', A: nec.scores_componentes.impacto || 0 },
                          { subject: 'V', A: nec.scores_componentes.vulnerabilidade || 0 },
                          { subject: 'C', A: nec.scores_componentes.capacidade || 0 },
                          { subject: 'E', A: nec.scores_componentes.equidade || 0 },
                        ]}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                          <Radar dataKey="A" fill={getScoreColor(nec.score_priorizacao || 0)} fillOpacity={0.5} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-600">{nec.explicacao_priorizacao || 'Sem análise'}</p>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    {nec.analise_ia?.recomendacao && (
                      <Badge className={
                        nec.analise_ia.recomendacao === 'Prioritário' ? 'bg-red-500' :
                        nec.analise_ia.recomendacao === 'Normal' ? 'bg-blue-500' :
                        'bg-green-500'
                      }>
                        IA: {nec.analise_ia.recomendacao}
                      </Badge>
                    )}
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); aprovarNecessidade(nec.id); }}
                        className="text-green-600"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setSelectedNecessidade(nec); setRejeitarModalOpen(true); }}
                        className="text-red-600"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={detalhesModalOpen} onOpenChange={setDetalhesModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Detalhes da Necessidade #{selectedNecessidade?.ranking}
            </DialogTitle>
            <DialogDescription>
              {selectedNecessidade?.instituicao_nome} - {selectedNecessidade?.tipo_recurso_nome}
            </DialogDescription>
          </DialogHeader>
          
          {detalhesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
            </div>
          ) : detalhesData && (
            <Tabs defaultValue="necessidade" className="flex-1">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="necessidade">Necessidade</TabsTrigger>
                <TabsTrigger value="analise">Análise Priorização</TabsTrigger>
                <TabsTrigger value="contexto">Contexto Instituição</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[500px] mt-4">
                <TabsContent value="necessidade" className="space-y-4">
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-500">Recurso</Label>
                          <p className="font-medium">{detalhesData.tipo_recurso?.nome}</p>
                          <Badge variant="outline">{detalhesData.tipo_recurso?.categoria}</Badge>
                        </div>
                        <div>
                          <Label className="text-slate-500">Quantidade</Label>
                          <p className="text-2xl font-bold">{detalhesData.necessidade?.quantidade_necessaria}</p>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-slate-500">Justificação</Label>
                        <p className="bg-slate-50 p-3 rounded-lg">{detalhesData.necessidade?.justificacao || 'Sem justificação'}</p>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-500">Beneficiários</Label>
                          <p className="font-medium">{detalhesData.necessidade?.num_beneficiarios || 'N/D'}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Destino</Label>
                          <p className="font-medium">{detalhesData.necessidade?.destino || 'Instituição'}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Data Necessidade</Label>
                          <p className="font-medium">{detalhesData.necessidade?.data_necessidade || 'N/D'}</p>
                        </div>
                      </div>
                      
                      <div className={`p-4 rounded-lg ${detalhesData.tem_stock ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          <Package className={`w-5 h-5 ${detalhesData.tem_stock ? 'text-green-600' : 'text-red-600'}`} />
                          <span className="font-medium">
                            {detalhesData.tem_stock 
                              ? `Stock disponível: ${detalhesData.total_stock_disponivel} unidades`
                              : 'Sem stock disponível'
                            }
                          </span>
                        </div>
                        {detalhesData.stock_disponivel?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {detalhesData.stock_disponivel.map((s, i) => (
                              <p key={i} className="text-sm text-slate-600">
                                {s.armazem}: {s.quantidade_disponivel} ({s.condicao})
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="analise" className="space-y-4">
                  {/* Score Final */}
                  <div className="text-center py-6 bg-gradient-to-br from-slate-50 to-white rounded-xl">
                    <p className="text-slate-500 mb-2">Score Final de Priorização</p>
                    <p 
                      className="text-6xl font-bold"
                      style={{ color: getScoreColor(selectedNecessidade?.score_priorizacao || 0) }}
                    >
                      {(selectedNecessidade?.score_priorizacao || 0).toFixed(1)}
                    </p>
                    <p className="text-lg mt-2">Ranking: #{selectedNecessidade?.ranking}</p>
                  </div>
                  
                  {/* Radar Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Análise Multi-Critério</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={getRadarData()}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="subject" />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} />
                            <Radar 
                              name="Score" 
                              dataKey="A" 
                              stroke={getScoreColor(selectedNecessidade?.score_priorizacao || 0)} 
                              fill={getScoreColor(selectedNecessidade?.score_priorizacao || 0)} 
                              fillOpacity={0.5} 
                            />
                            <Legend />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Scores Table */}
                  <Card>
                    <CardContent className="pt-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Critério</TableHead>
                            <TableHead className="text-center">Score</TableHead>
                            <TableHead className="text-center">Peso</TableHead>
                            <TableHead className="text-center">Contribuição</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedNecessidade?.scores_componentes && Object.entries(selectedNecessidade.scores_componentes).map(([key, value]) => {
                            const pesos = { urgencia: 0.30, impacto: 0.25, vulnerabilidade: 0.20, capacidade: 0.15, equidade: 0.10 };
                            const contribuicao = (value * pesos[key]).toFixed(1);
                            return (
                              <TableRow key={key}>
                                <TableCell className="capitalize font-medium">{key}</TableCell>
                                <TableCell className="text-center">
                                  <Badge style={{ backgroundColor: getScoreColor(value) }} className="text-white">
                                    {value.toFixed(0)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">{(pesos[key] * 100).toFixed(0)}%</TableCell>
                                <TableCell className="text-center font-bold">{contribuicao}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                  
                  {/* IA Analysis */}
                  {selectedNecessidade?.analise_ia && (
                    <Card className="border-purple-200 bg-purple-50/50">
                      <CardHeader>
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          Análise IA (Claude)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className={
                            selectedNecessidade.analise_ia.recomendacao === 'Prioritário' ? 'bg-red-500' :
                            selectedNecessidade.analise_ia.recomendacao === 'Normal' ? 'bg-blue-500' :
                            'bg-green-500'
                          }>
                            {selectedNecessidade.analise_ia.recomendacao}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            Urgência IA: +{selectedNecessidade.analise_ia.score_urgencia_ia || 0} | 
                            Vulnerabilidade IA: +{selectedNecessidade.analise_ia.score_vulnerabilidade_ia || 0}
                          </span>
                        </div>
                        <p className="text-sm">{selectedNecessidade.analise_ia.analise}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
                
                <TabsContent value="contexto" className="space-y-4">
                  {/* Institution Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Dados da Instituição</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-500">Nome</Label>
                          <p className="font-medium">{detalhesData.instituicao?.nome}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Código EA</Label>
                          <p className="font-medium">{detalhesData.instituicao?.codigo_ea}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Tipo</Label>
                          <p className="font-medium">{detalhesData.instituicao?.tipo_entidade}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Distrito</Label>
                          <p className="font-medium">{detalhesData.distrito?.nome}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Situação Financeira</Label>
                          <Badge className={
                            detalhesData.instituicao?.situacao_financeira === 'Deficitária' ? 'bg-red-500' :
                            detalhesData.instituicao?.situacao_financeira === 'Equilibrada' ? 'bg-blue-500' :
                            'bg-green-500'
                          }>
                            {detalhesData.instituicao?.situacao_financeira}
                          </Badge>
                        </div>
                        <div>
                          <Label className="text-slate-500">Técnicos / Voluntários</Label>
                          <p className="font-medium">
                            {detalhesData.instituicao?.num_tecnicos} / {detalhesData.instituicao?.num_voluntarios}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Respostas Sociais */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Respostas Sociais Ativas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {detalhesData.instituicao?.valencias?.map((val, i) => (
                          <Badge key={i} variant="outline" className="py-1">
                            {val.nome} ({val.numero_utentes}/{val.capacidade_maxima})
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Utentes */}
                  {detalhesData.utentes && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Distribuição de Utentes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-4 gap-4 text-center">
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-blue-600">
                              {(detalhesData.utentes.bebes_0_12m_masculino || 0) + (detalhesData.utentes.bebes_0_12m_feminino || 0) +
                               (detalhesData.utentes.bebes_13_24m_masculino || 0) + (detalhesData.utentes.bebes_13_24m_feminino || 0) +
                               (detalhesData.utentes.criancas_3_5a_masculino || 0) + (detalhesData.utentes.criancas_3_5a_feminino || 0) +
                               (detalhesData.utentes.criancas_6_10a_masculino || 0) + (detalhesData.utentes.criancas_6_10a_feminino || 0)}
                            </p>
                            <p className="text-xs text-slate-500">Crianças (0-10)</p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-green-600">
                              {(detalhesData.utentes.jovens_11_14a_masculino || 0) + (detalhesData.utentes.jovens_11_14a_feminino || 0) +
                               (detalhesData.utentes.jovens_15_18a_masculino || 0) + (detalhesData.utentes.jovens_15_18a_feminino || 0)}
                            </p>
                            <p className="text-xs text-slate-500">Jovens (11-18)</p>
                          </div>
                          <div className="bg-amber-50 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-amber-600">
                              {(detalhesData.utentes.adultos_18plus_masculino || 0) + (detalhesData.utentes.adultos_18plus_feminino || 0)}
                            </p>
                            <p className="text-xs text-slate-500">Adultos (18+)</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="text-2xl font-bold text-purple-600">
                              {(detalhesData.utentes.idosos_65plus_masculino || 0) + (detalhesData.utentes.idosos_65plus_feminino || 0)}
                            </p>
                            <p className="text-xs text-slate-500">Idosos (65+)</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Histórico */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Histórico de Apoios (últimos 12 meses)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {detalhesData.historico_apoios?.length > 0 ? (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {detalhesData.historico_apoios.map((apoio, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <span className="text-sm">{new Date(apoio.created_at).toLocaleDateString('pt-PT')}</span>
                              <Badge variant="outline">{apoio.quantidade_alocada} unid.</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">Nenhum apoio registado</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setDetalhesModalOpen(false)}>Fechar</Button>
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => { setRejeitarModalOpen(true); }}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rejeitar
            </Button>
            <Button 
              className={`${detalhesData?.tem_stock ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              onClick={() => aprovarNecessidade(selectedNecessidade?.id)}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {detalhesData?.tem_stock ? 'Aprovar e Alocar' : 'Aprovar sem Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Modal */}
      <Dialog open={rejeitarModalOpen} onOpenChange={setRejeitarModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Necessidade</DialogTitle>
            <DialogDescription>
              Indique o motivo da rejeição
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Motivo</Label>
            <Input 
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              placeholder="Ex: Fora do escopo, dados incompletos..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejeitarModalOpen(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={rejeitarNecessidade}>
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
